/**
 * Sprint 24.17a — pickup-toast derivation (Pattern, Phase E).
 *
 * Pure mapping from the "Architect picked up Sprint X" Activity (src/engine/
 * the agent runtime) to the sticky pickup toast's content + phase progress. The
 * observer hook fires showPickupToast from this; keeping it pure makes the
 * phase→progress mapping + copy testable without React.
 *
 * Phase ranges (S24.17a.21): audit 0–30%, draft 30–70%, questions 70–100%. We
 * only ever know the current phase (not sub-progress), so each phase maps to a
 * representative point; terminal phases (approved/shipped) are 100%.
 */

import { rampToHex, type ActivityLike } from "./active-activity";

export interface PickupToastPayload {
  /** Stable id → replace-in-place (one toast per task pickup). */
  stableId: string;
  title: string;
  subtitle: string;
  /** 0–100; the toast hides the progress bar at 100. */
  progress: number;
  iconGlyph: string;
  iconRampHex: string;
}

const PHASE_PROGRESS: Record<string, number> = {
  audit: 15,
  draft: 50,
  questions: 85,
  "in-review": 85,
  approved: 100,
  shipped: 100,
  done: 100,
};

export function pickupPhaseProgress(phase: string | null | undefined): number {
  if (!phase) return 10;
  return PHASE_PROGRESS[phase] ?? 10;
}

interface PickupDetails {
  trigger?: string;
  task_id?: string;
  task_title?: string;
  phase?: string;
  question_count?: number;
  agent?: { name?: string; icon?: string; ramp?: string };
}

/**
 * Build the pickup-toast content for a sprint-pickup Activity, or null when the
 * Activity isn't a pickup. `phase` selects the title/subtitle per S24.17a.20/22:
 * draft phases announce the pickup, `in-review` announces surfaced questions,
 * `approved` announces readiness for dispatch (less prominent — progress 100).
 */
export function pickupToastFromActivity(activity: ActivityLike): PickupToastPayload | null {
  const d = (activity.details && typeof activity.details === "object" ? activity.details : null) as PickupDetails | null;
  if (!d || d.trigger !== "sprint-task-pickup" || !d.task_id) return null;

  const agent = d.agent ?? {};
  const agentName = agent.name ?? "Kuvio Architect";
  const sprint = d.task_title ?? "the sprint";
  const phase = d.phase ?? "audit";

  let title: string;
  let subtitle: string;
  if (phase === "approved" || phase === "shipped") {
    title = `${sprint} approved · ready for Sprint 24.17b dispatch`;
    subtitle = "";
  } else if (phase === "in-review" || phase === "questions") {
    const n = typeof d.question_count === "number" ? d.question_count : null;
    title = n !== null ? `${agentName} surfaced ${n} question${n === 1 ? "" : "s"} for ${sprint}` : `${agentName} surfaced questions for ${sprint}`;
    subtitle = "Waiting on your reply in chat";
  } else {
    title = `${agentName} picked up ${sprint}`;
    subtitle = phase === "draft" ? "Drafting the spec" : "Auditing codebase · drafting spec";
  }

  return {
    stableId: `pickup:${d.task_id}`,
    title,
    subtitle,
    progress: pickupPhaseProgress(phase),
    iconGlyph: agent.icon ?? "ti-compass",
    iconRampHex: rampToHex(agent.ramp),
  };
}
